import React, { useState, useCallback, useMemo } from 'react';
import { Tag, Typography, Tooltip, Space, Dropdown, Grid } from 'antd';
import {
    CommentOutlined,
    UserOutlined,
    RobotOutlined,
    ClockCircleOutlined,
    MoreOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowRightOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';

const { Text } = Typography;

const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

const STATUS_CONFIG = {
    open: { color: '#0958d9', bg: 'rgba(22, 119, 255, 0.04)', border: '#1677ff', dot: '#1677ff' },
    in_progress: { color: '#d46b08', bg: 'rgba(250, 140, 22, 0.04)', border: '#fa8c16', dot: '#fa8c16' },
    resolved: { color: '#389e0d', bg: 'rgba(82, 196, 26, 0.04)', border: '#52c41a', dot: '#52c41a' },
    closed: { color: '#595959', bg: 'rgba(140, 140, 140, 0.04)', border: '#8c8c8c', dot: '#8c8c8c' },
};

const STATUS_CONFIG_DARK = {
    open: { color: '#69b1ff', bg: 'rgba(22, 119, 255, 0.08)', border: '#4096ff', dot: '#4096ff' },
    in_progress: { color: '#ffd666', bg: 'rgba(250, 140, 22, 0.08)', border: '#ffa940', dot: '#ffa940' },
    resolved: { color: '#95de64', bg: 'rgba(82, 196, 26, 0.08)', border: '#73d13d', dot: '#73d13d' },
    closed: { color: '#8c8c8c', bg: 'rgba(140, 140, 140, 0.08)', border: '#595959', dot: '#595959' },
};

const PRIORITY_COLORS = {
    urgent: '#ff4d4f',
    high: '#fa8c16',
    medium: '#1677ff',
    low: '#bfbfbf',
};

const CATEGORY_TAG_COLORS = {
    escalation: 'volcano',
    reclamation: 'magenta',
    suggestion: 'cyan',
    order: 'geekblue',
    other: 'default',
};

const PRIORITY_TAG_COLORS = {
    low: 'default',
    medium: 'blue',
    high: 'orange',
    urgent: 'red',
};

/**
 * Returns a human-friendly relative time string.
 */
const timeAgo = (dateString) => {
    if (!dateString) return '';
    try {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 1) return 'now';
        if (diffMins < 60) return `${diffMins}m`;
        const diffHrs = Math.floor(diffMins / 60);
        if (diffHrs < 24) return `${diffHrs}h`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays < 30) return `${diffDays}d`;
        return `${Math.floor(diffDays / 30)}mo`;
    } catch {
        return '';
    }
};

/**
 * Returns a short display ID like "#a3b8" from a full hashed ID.
 */
const shortId = (id) => {
    if (!id) return '';
    return `#${String(id).slice(0, 5).toUpperCase()}`;
};

// --- TicketCard component ---
const TicketCard = ({ ticket, isDarkMode, onDragStart, onDragEnd, draggingId, onStatusChange }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const priorityColor = PRIORITY_COLORS[ticket.priority] || '#bfbfbf';
    const isDragging = draggingId === ticket.id;

    const cardThemeClass = isDarkMode ? 'card-dark' : 'card-light';

    const quickActions = useMemo(() => {
        const otherStatuses = STATUSES.filter(s => s !== ticket.status);
        return {
            items: [
                ...otherStatuses.map(s => ({
                    key: `move_${s}`,
                    label: t(`ticket_status_${s}`),
                    icon: <ArrowRightOutlined />,
                })),
                { type: 'divider' },
                {
                    key: 'edit',
                    label: t('edit_ticket'),
                    icon: <EditOutlined />,
                },
                {
                    key: 'delete',
                    label: t('delete'),
                    icon: <DeleteOutlined />,
                    danger: true,
                },
            ],
        };
    }, [ticket.status, t]);

    const handleMenuClick = useCallback(({ key, domEvent }) => {
        domEvent.stopPropagation();
        if (key.startsWith('move_')) {
            const newStatus = key.replace('move_', '');
            onStatusChange(ticket.id, { status: newStatus });
        } else if (key === 'edit') {
            navigate(`/tickets/${ticket.id}`);
        }
    }, [ticket.id, onStatusChange, navigate]);

    return (
        <div
            className={`kanban-card ${cardThemeClass}${isDragging ? ' dragging' : ''}`}
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', ticket.id);
                e.dataTransfer.effectAllowed = 'move';
                onDragStart(ticket.id);
            }}
            onDragEnd={onDragEnd}
            onClick={() => navigate(`/tickets/${ticket.id}`)}
            style={{
                background: isDarkMode ? '#141414' : '#ffffff',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.88)',
            }}
        >
            {/* Priority indicator bar */}
            <div className="card-priority-bar" style={{ background: priorityColor }} />

            {/* Header: ticket ID + actions */}
            <div className="card-header-row">
                <span className="card-ticket-id">{shortId(ticket.id)}</span>
                <Dropdown
                    menu={{ ...quickActions, onClick: handleMenuClick }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <span
                        className="card-actions-btn"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            cursor: 'pointer',
                            padding: '2px 4px',
                            borderRadius: 4,
                            fontSize: 14,
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)',
                        }}
                    >
                        <MoreOutlined />
                    </span>
                </Dropdown>
            </div>

            {/* Subject */}
            <div className="card-subject">
                {ticket.subject || t('no_subject')}
            </div>

            {/* Description preview */}
            {ticket.description && (
                <div className="card-description">
                    {ticket.description}
                </div>
            )}

            {/* Tags */}
            <div className="card-tags">
                {ticket.category && (
                    <Tag color={CATEGORY_TAG_COLORS[ticket.category] || 'default'}>
                        {t(`ticket_category_${ticket.category}`) || ticket.category}
                    </Tag>
                )}
                {ticket.priority && (
                    <Tag color={PRIORITY_TAG_COLORS[ticket.priority] || 'default'}>
                        {t(`ticket_priority_${ticket.priority}`) || ticket.priority}
                    </Tag>
                )}
            </div>

            {/* Footer */}
            <div className="card-footer">
                <Space size={8}>
                    {ticket.opened_by_type && (
                        <Tooltip title={t(`opened_by_${ticket.opened_by_type}`)}>
                            {ticket.opened_by_type === 'agent'
                                ? <RobotOutlined style={{ fontSize: 12 }} />
                                : <UserOutlined style={{ fontSize: 12 }} />
                            }
                        </Tooltip>
                    )}
                    {ticket.comment_count > 0 && (
                        <Space size={2}>
                            <CommentOutlined style={{ fontSize: 11 }} />
                            <span>{ticket.comment_count}</span>
                        </Space>
                    )}
                </Space>
                <Space size={2}>
                    <ClockCircleOutlined style={{ fontSize: 10 }} />
                    <span>{timeAgo(ticket.created_at)}</span>
                </Space>
            </div>
        </div>
    );
};

// --- KanbanColumn component ---
const KanbanColumn = ({ status, tickets, isDarkMode, onDrop, onDragStart, onDragEnd, draggingId, onStatusChange, t }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const config = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        const ticketId = e.dataTransfer.getData('text/plain');
        if (ticketId) {
            onDrop(ticketId, status);
        }
    }, [onDrop, status]);

    return (
        <div
            className="kanban-column"
            style={{
                background: config.bg,
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
            }}
        >
            <div
                className="kanban-column-header"
                style={{
                    borderBottomColor: config.border,
                    color: config.color,
                }}
            >
                <span className="column-title">
                    <span className="column-dot" style={{ background: config.dot }} />
                    <span>{t(`ticket_status_${status}`) || status}</span>
                </span>
                <span
                    className="column-count"
                    style={{
                        background: config.color,
                        color: '#fff',
                    }}
                >
                    {tickets.length}
                </span>
            </div>

            <div
                className={`kanban-column-body${isDragOver ? ' drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {tickets.length === 0 ? (
                    <div className={`kanban-empty-placeholder${isDarkMode ? ' dark' : ''}`}>
                        <InboxOutlined style={{ fontSize: 22, marginBottom: 6, opacity: 0.4 }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('no_tickets_in_column')}
                        </Text>
                    </div>
                ) : (
                    tickets.map(ticket => (
                        <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            isDarkMode={isDarkMode}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            draggingId={draggingId}
                            onStatusChange={onStatusChange}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

// --- Main KanbanBoard component ---
const KanbanBoard = ({ tickets, onStatusChange, skeleton }) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const screens = Grid.useBreakpoint();
    const isCompact = !screens.md;
    const [draggingId, setDraggingId] = useState(null);
    const [activeTab, setActiveTab] = useState('open');

    const grouped = useMemo(() => {
        const groups = {};
        STATUSES.forEach(s => { groups[s] = []; });
        (tickets || []).forEach(ticket => {
            const status = ticket.status || 'open';
            if (groups[status]) {
                groups[status].push(ticket);
            } else {
                groups.open.push(ticket);
            }
        });
        return groups;
    }, [tickets]);

    const handleDrop = useCallback((ticketId, newStatus) => {
        const ticket = tickets.find(t => t.id === ticketId);
        if (ticket && ticket.status !== newStatus) {
            onStatusChange(ticketId, { status: newStatus });
        }
        setDraggingId(null);
    }, [tickets, onStatusChange]);

    const handleDragStart = useCallback((id) => {
        setDraggingId(id);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggingId(null);
    }, []);

    if (skeleton) {
        return (
            <div className={isCompact ? 'kanban-board-mobile' : 'kanban-board'}>
                {isCompact ? (
                    <>
                        <div className="kanban-mobile-tabs">
                            {STATUSES.map(s => {
                                const cfg = isDarkMode ? STATUS_CONFIG_DARK[s] : STATUS_CONFIG[s];
                                return (
                                    <div key={s} className="kanban-mobile-tab" style={{ opacity: 0.5 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, display: 'inline-block' }} />
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ padding: 8 }}>
                            {[1, 2, 3].map(i => (
                                <div key={i} className="kanban-skeleton-card" style={{ background: isDarkMode ? '#1f1f1f' : '#f5f5f5', height: 80, borderRadius: 10, marginBottom: 8 }} />
                            ))}
                        </div>
                    </>
                ) : (
                    STATUSES.map(status => {
                        const config = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];
                        return (
                            <div key={status} className="kanban-column" style={{ background: config.bg, border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
                                <div className="kanban-column-header" style={{ borderBottomColor: config.border, color: config.color }}>
                                    <span className="column-title">
                                        <span className="column-dot" style={{ background: config.dot }} />
                                        <span>{t(`ticket_status_${status}`)}</span>
                                    </span>
                                    <span className="column-count" style={{ background: config.color, color: '#fff' }}>—</span>
                                </div>
                                <div className="kanban-column-body">
                                    {[1, 2].map(i => (
                                        <div key={i} className="kanban-skeleton-card" style={{ background: isDarkMode ? '#1f1f1f' : '#f5f5f5', height: 90, borderRadius: 10 }} />
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        );
    }

    /* ─── Mobile: Tabbed Layout ─── */
    if (isCompact) {
        const activeConfig = isDarkMode ? STATUS_CONFIG_DARK[activeTab] : STATUS_CONFIG[activeTab];
        const activeTickets = grouped[activeTab] || [];

        return (
            <div className="kanban-board-mobile">
                {/* Status Tab Bar */}
                <div className="kanban-mobile-tabs">
                    {STATUSES.map(status => {
                        const cfg = isDarkMode ? STATUS_CONFIG_DARK[status] : STATUS_CONFIG[status];
                        const count = grouped[status]?.length || 0;
                        const isActive = activeTab === status;
                        return (
                            <div
                                key={status}
                                className={`kanban-mobile-tab${isActive ? ' active' : ''}`}
                                onClick={() => setActiveTab(status)}
                                style={{
                                    borderBottomColor: isActive ? cfg.border : 'transparent',
                                    color: isActive ? cfg.color : (isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'),
                                }}
                            >
                                <span style={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    background: cfg.dot, display: 'inline-block',
                                    opacity: isActive ? 1 : 0.5,
                                }} />
                                <span className="kanban-mobile-tab-label">
                                    {t(`ticket_status_${status}`)}
                                </span>
                                <span
                                    className="kanban-mobile-tab-count"
                                    style={{
                                        background: isActive ? cfg.color : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'),
                                        color: isActive ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'),
                                    }}
                                >
                                    {count}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Cards for active status */}
                <div
                    className="kanban-mobile-body"
                    style={{ background: activeConfig.bg }}
                >
                    {activeTickets.length === 0 ? (
                        <div className={`kanban-empty-placeholder${isDarkMode ? ' dark' : ''}`}>
                            <InboxOutlined style={{ fontSize: 22, marginBottom: 6, opacity: 0.4 }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('no_tickets_in_column')}
                            </Text>
                        </div>
                    ) : (
                        activeTickets.map(ticket => (
                            <TicketCard
                                key={ticket.id}
                                ticket={ticket}
                                isDarkMode={isDarkMode}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                draggingId={draggingId}
                                onStatusChange={onStatusChange}
                            />
                        ))
                    )}
                </div>
            </div>
        );
    }

    /* ─── Desktop: Normal Columns ─── */
    return (
        <div className="kanban-board">
            {STATUSES.map(status => (
                <KanbanColumn
                    key={status}
                    status={status}
                    tickets={grouped[status]}
                    isDarkMode={isDarkMode}
                    onDrop={handleDrop}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    draggingId={draggingId}
                    onStatusChange={onStatusChange}
                    t={t}
                />
            ))}
        </div>
    );
};

export default KanbanBoard;
